import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Star } from 'lucide-react';
import './AgencyRatingStars.css';

// Match the /homecare rating interaction while keeping agency claims separate.
const SLIDES = [
  { avatarSrc: '/nurse-one.jpg', objectPosition: '50% 20%', title: 'Trusted by 1,500+ agencies', subtitle: 'Five-star software for home-care and healthcare teams.' },
  { avatarSrc: '/care-slide-one.jpg', objectPosition: '58% 18%', title: 'Rated five stars', subtitle: 'A platform for the people who keep agency operations moving.' },
  { avatarSrc: '/trust-homecare.png', objectPosition: '30% 18%', title: 'Your operations, connected', subtitle: 'Scheduling, time tracking, payroll and invoicing in one place.' },
  { avatarSrc: '/right-care.png', objectPosition: '68% 22%', title: 'Built around your agency', subtitle: 'Keep the day-to-day work of your agency together in Worky.' },
  { avatarSrc: '/homecare/07-faq-human-coordinator.jpg', objectPosition: '28% 12%', title: 'A growing agency community', subtitle: 'Join more than 1,500 agencies using Worky.' },
] as const;

export const AgencyRatingStars = (): React.ReactElement => {
  const [visible, setVisible] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const slide = SLIDES[slideIndex];

  useEffect(() => {
    if (!visible) return undefined;
    const interval = window.setInterval(() => setSlideIndex(index => (index + 1) % SLIDES.length), 4000);
    return () => window.clearInterval(interval);
  }, [visible]);

  const place = (x: number, y: number): void => {
    const width = Math.min(400, window.innerWidth - 24);
    const height = tooltipRef.current?.offsetHeight ?? 100;
    const preferredY = window.innerWidth <= 480 ? y - height - 18 : y + 18;
    setPosition({
      x: Math.max(12, Math.min(x + 18, window.innerWidth - width - 12)),
      y: Math.max(12, Math.min(preferredY, window.innerHeight - height - 12)),
    });
  };
  const showAtTrigger = (): void => {
    const bounds = triggerRef.current?.getBoundingClientRect();
    if (bounds) place(bounds.left + bounds.width / 2, bounds.bottom);
    setSlideIndex(0);
    setVisible(true);
  };
  const hide = (): void => { setVisible(false); setSlideIndex(0); };

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className='agency-rating-stars-trigger'
        aria-label='Rated 5 out of 5 stars. Trusted by more than 1,500 agencies.'
        aria-describedby={visible ? tooltipId : undefined}
        onMouseEnter={event => { place(event.clientX, event.clientY); setSlideIndex(0); setVisible(true); }}
        onMouseMove={event => place(event.clientX, event.clientY)}
        onMouseLeave={hide}
        onFocus={showAtTrigger}
        onBlur={hide}
        onClick={showAtTrigger}
        onKeyDown={event => { if (event.key === 'Escape') hide(); }}
      >
        {Array.from({ length: 5 }, (_, index) => <Star key={index} fill='currentColor' aria-hidden={true} />)}
      </button>
      {visible && createPortal(
        <div ref={tooltipRef} id={tooltipId} role='tooltip' className='agency-rating-tooltip' style={{ left: position.x, top: position.y }}>
          <div key={slide.title} className='agency-rating-tooltip-inner'>
            <img className='agency-rating-tooltip-avatar' src={`${process.env.PUBLIC_URL}${slide.avatarSrc}`} alt='' style={{ objectPosition: slide.objectPosition }} />
            <div className='agency-rating-tooltip-copy'>
              <div className='agency-rating-tooltip-title'>{slide.title}</div>
              <div className='agency-rating-tooltip-subtitle'>{slide.subtitle}</div>
              <div className='agency-rating-tooltip-stars'>
                {Array.from({ length: 5 }, (_, index) => <Star key={index} size={14} fill='#FBBC04' color='#FBBC04' aria-hidden={true} />)}
              </div>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
};
